import SectionHeading from '@/components/base/SectionHeading';
import ScrollReveal from '@/components/base/ScrollReveal';
import ArticleLink from '@/components/feature/ArticleLink';
import { riskCase } from '@/mocks/riskManagementPage';

export default function RiskCase() {
  return (
    <section
      id="risk-case"
      className="scroll-mt-[7.5rem] border-b border-background-200 bg-background-100/60 md:scroll-mt-[9rem]"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="ตัวอย่างการสอน"
          title="ตัวอย่างการคำนวณความเสี่ยงในการเทรด"
          subtitle="ลองไล่ตัวเลขทีละขั้นจากเงินทุนไปจนถึงขนาดสถานะ จะเห็นว่าความเสี่ยงที่กำหนดไว้กลายเป็นเพดานขาดทุนของออเดอร์ได้อย่างไร"
        />

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-12 lg:grid-cols-4 lg:gap-5">
          {riskCase.steps.map((step, index) => (
            <ScrollReveal
              as="article"
              key={step.no}
              delay={index * 70}
              className="relative flex flex-col rounded-xl border border-background-200 bg-background-50 p-5"
            >
              <div className="flex items-center justify-between">
                <span className="font-num text-xs font-semibold text-foreground-400">{step.no}</span>
                <span className="text-[10px] font-medium uppercase tracking-wide text-foreground-400">
                  ขั้นที่ {index + 1}
                </span>
              </div>
              <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-950">{step.title}</h3>
              <p className="mt-2 font-num text-xl font-semibold leading-tight text-primary-700">{step.value}</p>
              <p className="mt-2.5 text-[12px] leading-relaxed text-foreground-600">{step.body}</p>
            </ScrollReveal>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[1.05fr_0.95fr] lg:gap-5">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">แผนภาพเงินทุน</p>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className="font-num text-2xl font-semibold text-foreground-950">{riskCase.account}</p>
                <p className="text-[11px] text-foreground-500">เงินทุนในบัญชี</p>
              </div>
              <div className="text-right">
                <p className="font-num text-lg font-semibold text-accent-800">{riskCase.riskAmount}</p>
                <p className="text-[11px] text-foreground-500">ความเสี่ยง {riskCase.riskPercent} ตามแผน</p>
              </div>
            </div>
            <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-background-200">
              <span className="h-full w-[10%] rounded-full bg-accent-500" aria-hidden="true" />
            </div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-background-200 pt-3.5 text-[11px] text-foreground-600">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm bg-background-300" aria-hidden="true" />
                เงินทุนส่วนที่ไม่ได้ใช้เสี่ยงในออเดอร์นี้
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm bg-accent-500" aria-hidden="true" />
                เพดานขาดทุนของออเดอร์
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-primary-200 bg-primary-50/70 p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-800">
                จากความเสี่ยงไปสู่ขนาดสถานะ
              </p>
              <p className="mt-2 rounded-md bg-background-50 px-3 py-2.5 font-num text-[12px] leading-relaxed text-foreground-800">
                ขนาดสถานะ = 100 USD ÷ (ระยะ Stop Loss × มูลค่าต่อจุด)
              </p>
              <p className="mt-2.5 text-[12px] leading-relaxed text-foreground-700">
                ยังไม่สามารถระบุ Lot Size จากตัวอย่างนี้ได้ เพราะต้องรู้คู่เงิน จุดเข้า ระยะ Stop Loss
                ข้อมูลสัญญา และสกุลเงินของบัญชีจริงก่อน
              </p>
            </div>

            <div className="flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50/70 p-4">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base text-accent-800" aria-hidden="true" />
              <p className="text-[12px] leading-relaxed text-foreground-700">{riskCase.note}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">อ่านวิธีคำนวณต่อ</p>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {riskCase.links.map((link) => (
              <li key={link.slug}>
                <ArticleLink
                  to={`/${link.slug}`}
                  className="group/link flex items-center gap-2 rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-3 text-[13px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-50 hover:text-primary-700"
                  aria-label={`เปิดบทความ ${link.title}`}
                >
                  <i className="ri-book-open-line shrink-0 text-base text-primary-600" aria-hidden="true" />
                  <span className="min-w-0 flex-1 leading-snug">{link.title}</span>
                  <i className="ri-arrow-right-up-line shrink-0 text-sm text-foreground-300" aria-hidden="true" />
                </ArticleLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}